import React from 'react';
import ReactSelect, {
  Props as ReactSelectProps,
  components,
  StylesConfig,
} from 'react-select';
import cx from 'classnames';

import ChevronS1Down from '../Icons/ChevronS1Down';
import CloseS2 from '../Icons/CloseS2';
import CloseS1 from '../Icons/CloseS1';
import Checkmark from '../Icons/Checkmark';
import { InputAlert } from '../BaseInput';

import reactSelectStyles from './Select.styles';
import baseStyles from '../BaseInput/BaseInput.module.scss';
import styles from './Select.module.scss';

interface OverrideProps {
  label: string;
  id: string;
  errorMessage?: string;
  onChange?: (e: any) => void | undefined;
}

interface OwnProps {
  hasMultiSelectBlur?: boolean;
  controlClassName?: Parameters<typeof cx>[0];
  isDarkMode?: boolean;
  disabled?: boolean;
  labelClassName?: string;
  customStyles?: StylesConfig;
  /** When true, the menu will be relatively positioned and remain in the normal flow of the document. */
  hasMenuRenderedInline?: boolean;
  /** Specify the height of the select. */
  size?: 40 | 48 | 56;
}

export type Props = Pick<
  ReactSelectProps,
  Exclude<keyof ReactSelectProps, 'id' | 'label' | 'onChange'>
> &
  OverrideProps &
  OwnProps;

// @ts-ignore
const Control = props => (
  <components.Control
    className={props.selectProps.controlClassName}
    {...props}
  />
);

// @ts-ignore
const SelectContainer = props => {
  const invalid = Boolean(props.selectProps.errorMessage);
  const alertId = `${props.selectProps.id}-alert`;
  const inputId = `${props.selectProps.id}-input`;

  return (
    <fieldset className={cx(baseStyles.container, props.selectProps.className)}>
      <components.SelectContainer {...props} inputId={inputId} className={null}>
        <label
          className={cx(
            baseStyles.label,
            styles &&
              styles[
                `label${props.selectProps.size ? props.selectProps.size : 48}`
              ],
            props.selectProps.isDarkMode && baseStyles.labelDark,
            (props.hasValue ||
              props.isFocused ||
              props.selectProps.placeholder) && [
              baseStyles.floatLabel,
              props.selectProps.isDarkMode && baseStyles.floatLabelDark,
            ],
            props.selectProps.labelClassName,
          )}
          htmlFor={inputId}
        >
          {props.selectProps.label}
        </label>
        {props.children}
      </components.SelectContainer>
      {invalid && (
        <InputAlert id={alertId} isDarkMode={props.selectProps.isDarkMode}>
          {props.selectProps.errorMessage}
        </InputAlert>
      )}
    </fieldset>
  );
};

// @ts-ignore
const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <ChevronS1Down />
  </components.DropdownIndicator>
);

// @ts-ignore
const ClearIndicator = props => (
  <components.ClearIndicator {...props}>
    <CloseS1 />
  </components.ClearIndicator>
);

// @ts-ignore
const MultiValueRemove = props => (
  <components.MultiValueRemove {...props}>
    <CloseS2 className={cx(styles.multiValueRemove)} />
  </components.MultiValueRemove>
);

// @ts-ignore
const Option = props => (
  <components.Option {...props}>
    {props.children} {props.isSelected ? <Checkmark /> : null}
  </components.Option>
);

// @ts-ignore
const Input = props => {
  const invalid = Boolean(props.selectProps.errorMessage);
  const errorId = `${props.selectProps.id}-alert`;

  return (
    <components.Input
      {...props}
      className={cx(props.className, styles.input)}
      aria-describedby={invalid ? errorId : undefined}
      aria-invalid={invalid ? 'true' : undefined}
    />
  );
};

export const Select: React.FC<Props> = props => {
  const inputId = `${props.id}-input`;
  return (
    <ReactSelect
      {...props}
      inputId={inputId}
      blurInputOnSelect={!props.isMulti ? true : props.hasMultiSelectBlur}
      placeholder={props.placeholder ? props.placeholder : ''}
      isClearable={props.isClearable}
      isDisabled={props['disabled']}
      openMenuOnFocus={true}
      tabSelectsValue={false}
      closeMenuOnSelect={
        props.closeMenuOnSelect != null
          ? props.closeMenuOnSelect
          : !props.isMulti
      }
      hideSelectedOptions={props.hideSelectedOptions}
      styles={props.customStyles || reactSelectStyles}
      components={{
        ClearIndicator,
        Control,
        DropdownIndicator,
        Input,
        MultiValueRemove,
        Option,
        SelectContainer,
      }}
    />
  );
};

Select.displayName = 'Select';
export default Select;
export { styles };
